/**
 * @fileoverview Subscription checkout success page.
 * @summary Displays success message after successful Stripe checkout.
 *
 * @package KaraoNow
 * @module apps/front/src/pages/organizer/SubscriptionSuccessPage
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 * @license EULA
 */

import { useTranslation } from 'react-i18next'
import { useSearchParams, useNavigate } from 'react-router-dom'
import PageHeader from '../../components/PageHeader'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheckCircle, faExclamationCircle } from '@fortawesome/free-solid-svg-icons'
import { SubscriptionType } from '@karaonow/shared'
import type { FC } from 'react'
import { useEffect } from 'react'

/**
 * @summary Subscription success page component.
 * @returns {ReactElement} Success page.
 */
const SubscriptionSuccessPage: FC = () => {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  const status = searchParams.get('status')
  const sessionId = searchParams.get('session_id')
  const planType = searchParams.get('plan') // 'premium' or 'one_shot'
  const eventId = searchParams.get('event') // For ONE_SHOT events

  const isSuccess = status === 'success'
  const isOneShotPlan = planType === SubscriptionType.ONE_SHOT

  // Redirect after 5 seconds
  useEffect(() => {
    if (!isSuccess) return

    const timer = setTimeout(() => {
      if (isOneShotPlan && eventId) {
        // Redirect to event management page for ONE_SHOT
        navigate(`/events/${eventId}`)
      } else {
        // Redirect to subscription page for PREMIUM
        navigate('/subscription')
      }
    }, 5000)

    return () => clearTimeout(timer)
  }, [navigate, isSuccess, isOneShotPlan, eventId])

  return (
    <div className="min-h-screen bg-linear-to-br from-slate-50 to-slate-100">
      <PageHeader
        title={isSuccess ? t('subscription.successTitle') : t('subscription.cancelledTitle')}
        subtitle={isSuccess ? t('subscription.successSubtitle') : t('subscription.cancelledSubtitle')}
      />

      <div className="container mx-auto px-4 py-12">
        <div className="max-w-2xl mx-auto">
          {/* Success Message */}
          {isSuccess ? (
            <div className="bg-green-50 border-2 border-green-200 rounded-lg p-12 text-center">
              <FontAwesomeIcon
                icon={faCheckCircle}
                className="text-6xl text-green-600 mb-6"
              />
              <h2 className="text-3xl font-bold text-green-800 mb-4">
                {t('subscription.paymentSuccessful')}
              </h2>
              <p className="text-green-700 text-lg mb-6">
                {t('subscription.paymentProcessing')}
              </p>
              <div className="bg-white p-4 rounded border border-green-200 mb-6">
                <p className="text-sm text-gray-600 mb-1">
                  <strong>Session ID:</strong>
                </p>
                <p className="text-xs font-mono text-gray-500 break-all">
                  {sessionId}
                </p>
              </div>
              <p className="text-gray-600 mb-8">
                {t('subscription.redirectingIn5')}
              </p>
              <button
                onClick={() => {
                  if (isOneShotPlan && eventId) {
                    navigate(`/events/${eventId}`)
                  } else {
                    navigate('/subscription')
                  }
                }}
                className="px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold transition-colors"
              >
                {isOneShotPlan
                  ? t('events.backToEvent') || 'Back to Event'
                  : t('subscription.backToSubscription')}
              </button>
            </div>
          ) : (
            /* Cancelled Message */
            <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-12 text-center">
              <FontAwesomeIcon
                icon={faExclamationCircle}
                className="text-6xl text-yellow-600 mb-6"
              />
              <h2 className="text-3xl font-bold text-yellow-800 mb-4">
                {t('subscription.paymentCancelled')}
              </h2>
              <p className="text-yellow-700 text-lg mb-8">
                {t('subscription.checkoutCancelledMessage')}
              </p>
              <button
                onClick={() => navigate('/subscription')}
                className="px-6 py-3 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg font-semibold transition-colors"
              >
                {t('subscription.backToSubscription')}
              </button>
            </div>
          )}

          {/* Info Box */}
          <div className="mt-12 p-6 bg-blue-50 border border-blue-200 rounded-lg">
            <h3 className="font-semibold text-blue-900 mb-3">
              {t('subscription.whatHappensNext')}
            </h3>
            <ul className="text-blue-800 space-y-2">
              <li>✅ {t('subscription.webhooksProcessing')}</li>
              <li>✅ {t('subscription.subscriptionActive')}</li>
              <li>✅ {t('subscription.confirmationEmail')}</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SubscriptionSuccessPage

